'use client';

import { useCallback, useEffect, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { mutate } from 'swr';
import useSdk from 'helpers/hooks/useSdk';
import useShopable from 'helpers/hooks/useShopable';
import { useCart } from 'frontastic';
import CtCheckoutForm from './components/CtCheckoutForm';
import ReservationGate from './components/ReservationGate';
import ShippingSelection, { ShippingMethod, PickupPoint } from './components/ShippingSelection';

const CommercetoolsCheckout = () => {
  const router = useRouter();
  const { locale } = useParams();
  const sdk = useSdk();
  const { data, isEmpty } = useCart();
  const shopable = useShopable();

  const [phase, setPhase] = useState<'reservations' | 'shipping' | 'saving' | 'checkout'>('reservations');
  const [selectedShippingMethod, setSelectedShippingMethod] = useState<ShippingMethod | null>(null);

  useEffect(() => {
    if (!shopable) {
      router.replace(`/${locale}/cart/?error=invalidCountry`);
      return;
    }
    // Only redirect for a real, loaded cart that is empty. A getCart that couldn't load the cart
    // (e.g. a transient backend hiccup while re-syncing the country) comes back as `{}` — no cartId —
    // which must not be mistaken for an empty cart, or it would bounce a non-empty cart to /cart.
    if (data?.cartId && isEmpty) {
      router.replace(`/${locale}/cart/?error=emptyCart`);
    }
  }, [shopable, data, isEmpty, router, locale]);

  const handleShippingComplete = useCallback(
    async (method: ShippingMethod, pickupPoint: PickupPoint | null) => {
      setPhase('saving');
      try {
        const res = await sdk.callAction({
          actionName: 'cart/setPickupPoint',
          payload: {
            pickupPoint: pickupPoint
              ? {
                  id: pickupPoint.id,
                  name: pickupPoint.name,
                  address: pickupPoint.address,
                  courier: pickupPoint.courier,
                  city: pickupPoint.city,
                  country: pickupPoint.country,
                  zip: pickupPoint.zip,
                  street: pickupPoint.street,
                }
              : null,
          },
        });
        if (!res || res.isError) {
          window.location.reload();
          return;
        }
        mutate('/action/cart/getCart', res);
        setSelectedShippingMethod(method);
        setPhase('checkout');
      } catch {
        window.location.reload();
      }
    },
    [sdk],
  );

  return (
    <div className="min-h-screen lg:bg-neutral-200">
      {phase === 'reservations' && <ReservationGate onSuccess={() => setPhase('shipping')} />}
      {phase === 'shipping' && <ShippingSelection onComplete={handleShippingComplete} />}
      {phase === 'saving' && (
        <div className="flex h-[80vh] items-center justify-center">
          <div className="loading-full-screen" />
        </div>
      )}
      {phase === 'checkout' && selectedShippingMethod && <CtCheckoutForm shippingMethod={selectedShippingMethod} />}
    </div>
  );
};

export default CommercetoolsCheckout;
